import { useCallback, useEffect, useState } from "react";
import { Config, driver as createDriver, DriveStep } from "driver.js";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setActiveStep } from "@store/driverSlice";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import sleep from "@utils/sleep";
import { useMediaQuery } from "react-responsive";
import { ScreenSize } from "@constants/screen-size";

const defaultConfig: Config = {
  showProgress: true,
};

const getSteps = (t: TFunction): DriveStep[] => [
  {
    element: "#edit-profile-button",
    popover: {
      title: t("guide.editProfile.title"),
      description: t("guide.editProfile.description"),
      side: "bottom",
    },
  },
  {
    element: "#menu-memories-gallery",
    popover: {
      title: t("guide.memoriesGallery.title"),
      description: t("guide.memoriesGallery.description"),
    },
  },
  {
    element: "#menu-schedule",
    popover: {
      title: t("guide.schedule.title"),
      description: t("guide.schedule.description"),
    },
  },
  {
    element: "#patient-mode-button",
    popover: {
      title: t("guide.patientMode.title"),
      description: t("guide.patientMode.description"),
      side: "bottom",
    },
  },
  {
    element: "#invite-patient-button",
    popover: {
      title: t("guide.invitePatient.title"),
      description: t("guide.invitePatient.description"),
      side: "bottom",
    },
  },
  {
    element: "#caregiver-call-button",
    popover: {
      title: t("guide.caregiverCall.title"),
      description: t("guide.caregiverCall.description"),
    },
  },
];

export const useDriver = () => {
  const { t } = useTranslation();
  const activeStep = useSelector((state: RootState) => state.driver.activeStep);
  const dispatch = useDispatch();
  const [initialStep, setInitialStep] = useState<number | undefined>();
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });

  useEffect(() => {
    if (initialStep !== undefined) {
      return;
    }
    setInitialStep(activeStep);
  }, [initialStep, activeStep]);

  const onStepChange = useCallback(
    (stepIndex: number | undefined) => {
      dispatch(setActiveStep(stepIndex || 0));
    },
    [dispatch],
  );

  useEffect(() => {
    if (isSmScreen) {
      return;
    }
    if (initialStep === undefined) {
      return;
    }
    const steps = getSteps(t);

    if (steps.length === initialStep) {
      return;
    }
    const driver = createDriver({
      ...defaultConfig,
      steps,
      onPrevClick: () => {
        onStepChange(driver.getActiveIndex()! - 1);
        driver.movePrevious();
      },
      onNextClick: () => {
        onStepChange(driver.getActiveIndex()! + 1);

        if (driver.isLastStep()) {
          driver.destroy();
          return;
        }
        driver.moveNext();
      },
      onCloseClick: () => {
        onStepChange(steps.length);
        driver.destroy();
      },
    });

    (async () => {
      while (!document.querySelector(steps[initialStep].element as string)) {
        await sleep(50);
      }
      driver.drive(initialStep);
    })();

    return () => {
      driver.destroy();
    };
  }, [initialStep, t, dispatch, onStepChange, isSmScreen]);
};
